import React, { useState, useEffect, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
	CodesToColorMapFunc,
	BgColorToTextColorMapFunc,
} from "../CodesToColorMap";
import {
	getResponse,
	saveResponse,
	deleteResponse,
	logActivity,
} from "../services/dashApiService";
import { BackButton } from "./common/BackButton";
import { NavFooter } from "./common/NavFooter";
import { NextButton } from "./common/NextButton";
import { ConfirmationButton } from "./common/ConfirmationButton";
import { HelpPopup } from "./HelpPopup";
import { ReportCodesToHelpCodesMap } from "../ReportCodesToHelpCodesMap";
import { QuestionnaireStartPageHeader } from "./QuestionnaireStartPageHeader";
import { LoadingSpinnerAlt } from "./common/LoadingSpinnerAlt";
import { ErrorModal } from "./common/ErrorModal";
import { ModalDialog } from "./common/ModalDialog";
import { Transition } from "@headlessui/react";
import { DelayLink } from "./common/DelayLink";
import { TransitionConfig } from "../configs/transitionConfig";
import { GenericModal } from "./common/GenericModal";
import { DiseaseGroupContext } from "../contexts/diseaseGroupContext";
import { FaCheck } from "react-icons/fa";
import { ArrowRight } from "./common/IconsV2";
import { toast } from "react-toastify";
import { toastConfig } from "../configs/toastConfig";

export const MultiQuestionnaire = () => {
	const location = useLocation();
	const history = useHistory();

	const { id, code, from: fromLocation } = location?.state ?? {};

	const { theme } = useContext(DiseaseGroupContext);

	const [finishText, setFinishText] = useState("Finish");

	const [showInformationDialogue, setShowInformationDialogue] = useState(false);
	const [information, setInformation] = useState([]);
	const [message, setMessage] = useState();

	const [currentTheme, setCurrentTheme] = useState(null);
	useEffect(() => {
		const color = CodesToColorMapFunc(code, theme) ?? theme.primary();
		setCurrentTheme(color);
	}, [code, theme]);

	const textColor = BgColorToTextColorMapFunc(currentTheme, "text");

	const [back] = useState(
		(code || "").indexOf("registration") === 0
			? "/dashboard/todo/nursereports"
			: "/dashboard"
	);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [preamble, setPreamble] = useState([
		"You don't need to complete these reports all at the same time. Come back and review or complete them when its convenient.",
		"Together they should not take you more than about 10 minutes",
	]);
	const [postamble, setPostamble] = useState([
		`When you have completed all reports, press '${finishText}' to complete this section.`,
	]);

	const [deleteButton, setDeleteButton] = useState(null);
	const [help, setHelp] = useState({});
	const [helpCode, setHelpCode] = useState(ReportCodesToHelpCodesMap[code]);

	const [response, setResponse] = useState();
	useEffect(() => {
		let didCancel = false;

		setIsLoading(true);
		getResponse(id, code)
			.then((result) => {
				if (!didCancel) {
					if (result.success) {
						const data = result.data;
						setResponse(data);

						if (data.questionnaires && data.questionnaires.length > 0) {
							if (data.defaults) {
								if (data.defaults.preamble) {
									setPreamble(data.defaults.preamble);
								}

								if (data.defaults.postamble) {
									setPostamble(data.defaults.postamble);
								}

								if (data.defaults.finishTextOverride) {
									setFinishText(data.defaults.finishTextOverride);
								}

								if (data.defaults.deleteButton) {
									setDeleteButton(data.defaults.deleteButton);
								}

								if (data.defaults.help) {
									setHelp(data.defaults.help);
								}

								if (data.defaults.helpCode) {
									setHelpCode(data.defaults.helpCode);
								}
							}
						} else {
							setHasError(true);
						}
					} else {
						setHasError(true);
					}
					setIsLoading(false);
				}
			})
			.catch((error) => {
				if (!didCancel) {
					setHasError(true);
					setIsLoading(false);
				}
			});

		return () => {
			didCancel = true;
		};
	}, [code, id]);

	const [isShowingHelp, setIsShowingHelp] = useState(false);

	const [isShowingErrorModal, setIsShowingErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState(
		"There was a problem fetching your data - please try again"
	);

	const [transitionInProgress, setTransitionInProgress] = useState(false);

	const [isShowingPage, setIsShowingPage] = useState(false);
	useEffect(() => {
		setIsShowingPage(true);
	}, []);

	const [hasComeFromQuestionnaire, setHasComeFromQuestionnaire] =
		useState(false);
	useEffect(() => {
		if (fromLocation && fromLocation.path === "/questionnaire") {
			setHasComeFromQuestionnaire(true);
		}
	}, [fromLocation]);

	const handleBack = (force) => {
		if (!force && transitionInProgress) {
			return;
		}

		setTransitionInProgress(true);
		history.push(back);
	};

	const closeInformationDialogue = () => {
		logActivity("info", "ack side-effects popup", message);
		setShowInformationDialogue(false);
		setInformation([]);
		setMessage();
		handleBack();
	};

	const handleFinish = (force) => {
		setTransitionInProgress(true);
		let remainingQuestionnaire = response.questionnaires.find(
			(q) => q.complete !== true
		);
		if (remainingQuestionnaire) {
			setErrorMessage(
				`Please complete the remaining questionnaires before submitting '${response.title}'.`
			);
			setIsShowingErrorModal(true);
			setTransitionInProgress(false);
		} else {
			setIsLoading(true);
			saveResponse(id, code, undefined, true, null, response.defaults.apiAction)
				.then((result) => {
					setIsLoading(false);
					setTransitionInProgress(false);

					if (result.data && result.data.message && result.data.message.msg) {
						if (result.data.message.popup) {
							const info = result.data.message.msg.map((m, index) => {
								const ret = [];
								switch (m.type) {
									case "list":
										ret.push(
											<ul className="list-disc ml-6" key={`ul_${index}`}>
												{m.body.map((b, index2) => {
													return (
														<li
															key={`ul_${index}_${index2}`}
															dangerouslySetInnerHTML={{ __html: b }}
														></li>
													);
												})}
											</ul>
										);
										break;

									case "link":
										ret.push(
											<p
												key={`p_${index}`}
												className={`${m.class || "mb-2"}`}
												dangerouslySetInnerHTML={{
													__html: `<span class="ss-icon ss-link text-md text-blue-600"}></span><span class="text-blue-600">${m.body}</span>`,
												}}
												onClick={() => {
													window.open(m.url, "_blank");
												}}
											></p>
										);
										break;

									case "text":
									default:
										for (const b of m.body) {
											ret.push(
												<p
													className="mb-2"
													key={`p_${index}`}
													dangerouslySetInnerHTML={{ __html: b }}
												></p>
											);
										}
										break;
								}

								return (
									<div
										key={`messageContainer_${index}`}
										className="w-full mb-3"
									>
										{ret}
									</div>
								);
							});

							setInformation(info);
							setMessage(result.data.message);
							setShowInformationDialogue(true);
						} else {
							handleBack(true);
							toast(result.data.message, {
								type: result.success ? "success" : "error",
							});
						}
					} else {
						handleBack(true);
					}
				})
				.catch((error) => {
					console.error("Error: ", error);
					setIsLoading(false);
					setTransitionInProgress(false);
					toast.error(
						<>
							<p className="mb-1">There was an error saving your data.</p>
							<p className="mb-1">
								Please make sure that you have a good connection to the internet
								and try again.
							</p>
							<p>
								If the problem persists, please contact our helpdesk, quoting
								reference '#MU001'.
							</p>
						</>,
						{
							toastId: toastConfig.toastIds.errNetwork,
						}
					);
				});
		}
	};

	const [currentWarningQuestionnaire, setCurrentWarningQuestionnaire] =
		useState(null);
	const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] =
		useState(false);

	const getDelayLinkOptions = (q) => {
		const ret = q
			? {
					pathname: "/questionnaire",
					state: {
						id: id,
						code: q.code,
						date: response.dueDate,
						bgColor: currentThemeColorBg,
						from: {
							path: location.pathname,
							code: code,
						},
					},
			  }
			: null;

		return ret;
	};

	const displayCompleteWarning = (questionnaire) => {
		setCurrentWarningQuestionnaire(questionnaire);
	};

	const clearCompleteWarning = () => {
		setCurrentWarningQuestionnaire(null);
	};

	const currentThemeColorBg = currentTheme ? currentTheme.bg() : "";

	const percentageComplete = () => {
		const allQuestionnaires = response?.questionnaires;

		const complete = allQuestionnaires?.filter(
			(q) => q.complete === true
		).length;

		const raw = (complete / allQuestionnaires?.length) * 100;

		return Math.round(raw);
	};

	const handleDelete = () => {
		setIsLoading(true);
		deleteResponse(id).then((res) => {
			if (res.success) {
				setIsLoading(false);
				setIsShowingDeleteConfirmation(false);
				setTransitionInProgress(true);
				history.push(back);
			} else {
				if (res.err) {
					console.error(`Dash API error: ${res.err.code}: ${res.err.msg}`);
				} else {
					console.error(
						`Unknown error occurred on deleteResponse. API response: ${res}`
					);
				}
				setHasError(true);
				setIsLoading(false);
			}
		});
	};

	let themeCodeOverride;
	if (
		response &&
		response.questionnaireCode &&
		response.questionnaireCode.startsWith("registration_")
	) {
		themeCodeOverride = response.questionnaireCode.replace("registration_", "");
	}

	return (
		<>
			{isLoading ? (
				<div className={`flex flex-col w-full h-full`}>
					<LoadingSpinnerAlt isFixed={true} color={"app-charcoal"} />
				</div>
			) : hasError ? (
				<div className={`flex flex-col w-full h-full justify-center px-8`}>
					<p className={`${textColor} text-center mb-16`}>{errorMessage}</p>
					<BackButton onClick={handleBack} variant="inactive">
						Back
					</BackButton>
				</div>
			) : showInformationDialogue ? (
				<ModalDialog show={showInformationDialogue} onClose={() => {}}>
					<div
						className={`relative bg-white text-black flex flex-col transition-all transform max-w-xl shadow-lg p-3 sm:p-4 rounded-lg`}
					>
						{information}
						{message.confirm ? (
							<div className="flex justify-items-center mt-3">
								<div className="w-full">
									<ConfirmationButton
										onClick={closeInformationDialogue}
										variant="active"
										showIcon={false}
										textOverride="OK"
										text={message.confirm}
									/>
								</div>
							</div>
						) : (
							<div className="flex justify-around mt-3">
								<div className="w-32">
									<BackButton
										onClick={closeInformationDialogue}
										variant="active"
										showIcon={false}
										textOverride="OK"
									/>
								</div>
							</div>
						)}
					</div>
				</ModalDialog>
			) : response ? (
				<div className={`fixed inset-0`}>
					<Transition
						className={`flex flex-col w-full h-full`}
						appear={true}
						show={isShowingPage}
						enter={`transform ease-in-out duration-${TransitionConfig.slideXTransition.enterDuration}`}
						enterFrom={
							hasComeFromQuestionnaire
								? "-translate-x-full"
								: "translate-x-full"
						}
						enterTo={"translate-x-0"}
						leave={`transition-opacity ease-in-out duration-${TransitionConfig.slideXTransition.leaveDuration}`}
						leaveFrom={"opacity-100"}
						leaveTo={"opacity-0"}
					>
						<QuestionnaireStartPageHeader
							preTitle={response.title}
							onHelpClick={
								helpCode || help.startPage ? () => setIsShowingHelp(true) : null
							}
							code={code}
							background={currentThemeColorBg}
							textColor={textColor}
							themeCodeOverride={themeCodeOverride}
						/>

						<div
							className={
								"flex-grow overflow-y-auto text-sm lg:text-base text-center bg-white text-black"
							}
						>
							<div className={"max-w-panel mx-auto"}>
								{preamble.map((p, index) => {
									return (
										<p key={`preamble_${index}`} className={"m-4"}>
											{p}
										</p>
									);
								})}

								<div className={"text-xl font-bold m-3"}>
									{percentageComplete()}% Complete
								</div>
								<div className={"px-6 pt-3 grid grid-cols-3 gap-x-2 pb-20"}>
									{response?.questionnaires?.map((q, i) => (
										<React.Fragment key={q.id}>
											<div className={"col-span-3 text-left"}>
												{q.complete ? (
													<button
														type="button"
														onClick={() => displayCompleteWarning(q)}
														className={`flex justify-between items-start w-full py-2 sm:py-3 block text-left text-base sm:text-lg border-b${
															i === 0 ? " border-t" : ""
														}`}
													>
														<span className={`flex`}>
															<span
																className={`flex flex-0-0-auto justify-center items-center w-5 h-5 text-white bg-app-notificationgreen rounded-full mt-0.5`}
															>
																<FaCheck size={10} />
															</span>
															<strong className={`px-3.5`}>
																Part&nbsp;{i + 1}:
															</strong>{" "}
															{q.name}
														</span>
														<ArrowRight
															className={"w-4 text-gray-400 ml-3.5 mt-1.5"}
														/>
													</button>
												) : (
													<DelayLink
														to={getDelayLinkOptions(q)}
														delay={
															TransitionConfig.slideXTransition.leaveDuration
														}
														onDelayStart={() => setIsShowingPage(false)}
														className={`flex justify-between items-start w-full py-2 sm:py-3 block text-left text-base sm:text-lg border-b${
															i === 0 ? " border-t" : ""
														}`}
													>
														<span className={`flex`}>
															<span
																className={`flex flex-0-0-auto relative justify-center items-center w-5 h-5 text-white bg-gray-300 rounded-full mt-0.5`}
															>
																<FaCheck size={10} />
																{q.complete === false && (
																	<span
																		className={`flex flex-0-0-auto absolute justify-center items-center w-2.5 h-2.5 text-white text-xxs bg-app-errorRed rounded-full leading-tight -top-1 -left-0.5`}
																	>
																		!
																	</span>
																)}
															</span>
															<strong className={`px-3.5`}>
																Part&nbsp;{i + 1}:
															</strong>{" "}
															{q.name}
														</span>
														<ArrowRight
															className={"w-4 text-gray-400 ml-3.5 mt-1.5"}
														/>
													</DelayLink>
												)}
											</div>
										</React.Fragment>
									))}

									{postamble.map((p, index) => {
										return (
											<p
												key={`postamble_${index}`}
												className={"col-span-4 mt-3"}
											>
												{p}
											</p>
										);
									})}
								</div>
							</div>
						</div>

						<div className={"absolute left-0 right-0 bottom-0"}>
							<NavFooter
								currentTheme={currentTheme}
								textColor={textColor}
								isBorderOff={true}
							>
								<BackButton
									onClick={handleBack}
									showIcon={false}
									variant="inactive"
									smaller
								/>
								{deleteButton && (
									<BackButton
										onClick={() => setIsShowingDeleteConfirmation(true)}
										textOverride={deleteButton?.text || "Delete"}
										showIcon={false}
										variant="inactive"
										smaller
									/>
								)}
								<NextButton
									onClick={handleFinish}
									textOverride={finishText}
									variant="active"
								/>
							</NavFooter>
						</div>
					</Transition>

					{/* Popups */}

					<ErrorModal
						show={isShowingErrorModal}
						onClose={() => setIsShowingErrorModal(false)}
						currentTheme={currentTheme}
						title={"INFORMATION"}
						text={errorMessage}
					/>

					<GenericModal
						show={currentWarningQuestionnaire ? true : false}
						onClose={() => clearCompleteWarning()}
						nextButton={
							<DelayLink
								to={getDelayLinkOptions(currentWarningQuestionnaire)}
								delay={TransitionConfig.slideXTransition.leaveDuration}
								className={"active-button block text-center"}
								children={"Continue"}
							/>
						}
						text={`If reviewing or editing please make sure you continue through to the completion page and press ${finishText} to re-save changes`}
						backText="Cancel"
					/>

					<HelpPopup
						show={isShowingHelp}
						onClose={() => setIsShowingHelp(false)}
						currentTheme={currentTheme}
						helpCode={helpCode || help.startPage}
					/>

					<GenericModal
						show={isShowingDeleteConfirmation}
						onClose={() => setIsShowingDeleteConfirmation(false)}
						onNext={handleDelete}
						nextText={"Delete"}
						text={"Are you sure you want to delete this registration?"}
						backText={"Cancel"}
					/>
				</div>
			) : (
				<div className={`flex flex-col w-full h-full justify-center`}>
					<p className={`text-center mb-16`}>{errorMessage}</p>
					<BackButton
						onClick={handleBack}
						showIcon={false}
						variant={"inactive"}
					/>
				</div>
			)}
		</>
	);
};
